import { useEffect, useMemo, useState } from "react";
import { Gift, Plus, Trash2, X } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { PackDto, PackListStatus } from "@gaole/shared";
import { resolvePackFoil } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { num, rm } from "../lib/format";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { useInfiniteCatalogProducts } from "../hooks/useInfiniteCatalogProducts";
import {
  Button,
  Input,
  NumberInput,
  SearchSelect,
  Badge,
  Toggle,
  Textarea,
  useConfirm,
  ColorField,
  parsePositiveInt,
} from "../components/ui";
import { ImageUploadField } from "../components/ImageUploadField";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";

const MODE_OPTIONS = [
  { value: "DECLINING", label: "Declining pool" },
  { value: "FIXED", label: "Fixed odds" },
] as const;

const STATUS_BADGE: Record<PackListStatus, { label: string; color: "green" | "gray" | "amber" | "pink" }> = {
  LISTED: { label: "Listed", color: "green" },
  DRAFT: { label: "Draft", color: "gray" },
  COMPLETE: { label: "Complete", color: "amber" },
  DELISTED: { label: "Delisted", color: "pink" },
};

interface ProductRef {
  id: string;
  name: string;
  behavior?: string;
  stockAvailable?: number;
  images?: string[];
}

interface TierItem {
  productId: string;
  qty: string;
  estValue?: number | null;
}

interface TierForm {
  name: string;
  color: string;
  weight: string;
  items: TierItem[];
}

interface OpenOfferForm {
  quantity: string;
  price: string;
  creditPrice: string;
}

interface PackDetail extends PackDto {
  tiers: {
    id: string;
    name: string;
    color: string;
    weight: number;
    oddsPct: number;
    items: {
      id: string;
      productId: string;
      name: string;
      initialQty: number;
      remainingQty: number;
      warehouseAvailable: number;
    }[];
  }[];
}

interface RestockTierItemForm {
  tierId: string;
  productId: string;
  qty: string;
}

const DEFAULT_TIER: TierForm = {
  name: "Common",
  color: "#5460ed",
  weight: "100",
  items: [{ productId: "", qty: "1" }],
};

const DEFAULT_OFFER: OpenOfferForm = { quantity: "1", price: "", creditPrice: "" };

function productTracksStock(product: ProductRef): boolean {
  return product.behavior !== "CREDIT_PACK" && (product.stockAvailable ?? 0) < 999_999;
}

function poolQtyByProduct(tiers: TierForm[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const tier of tiers) {
    for (const item of tier.items) {
      if (!item.productId || item.qty === "") continue;
      totals.set(item.productId, (totals.get(item.productId) ?? 0) + parsePositiveInt(item.qty, 0));
    }
  }
  return totals;
}

function committedQtyByProduct(pack: PackDetail | undefined): Map<string, number> {
  const totals = new Map<string, number>();
  if (!pack) return totals;
  for (const tier of pack.tiers) {
    for (const item of tier.items) {
      totals.set(item.productId, (totals.get(item.productId) ?? 0) + item.remainingQty);
    }
  }
  return totals;
}

function effectiveStockForPool(product: ProductRef, poolCommitted: Map<string, number>): number {
  return (product.stockAvailable ?? 0) + (poolCommitted.get(product.id) ?? 0);
}

function restockQtyByProduct(
  pack: PackDetail | undefined,
  addQtyByItem: Record<string, string>,
  restockNewItems: RestockTierItemForm[],
  restockNewTiers: TierForm[],
): Map<string, number> {
  const totals = new Map<string, number>();
  if (!pack) return totals;

  const itemById = new Map(pack.tiers.flatMap((t) => t.items).map((i) => [i.id, i]));
  for (const [packItemId, qtyStr] of Object.entries(addQtyByItem)) {
    if (!qtyStr.trim()) continue;
    const item = itemById.get(packItemId);
    if (!item) continue;
    totals.set(item.productId, (totals.get(item.productId) ?? 0) + parsePositiveInt(qtyStr, 0));
  }
  for (const row of restockNewItems) {
    if (!row.productId || !row.qty.trim()) continue;
    totals.set(row.productId, (totals.get(row.productId) ?? 0) + parsePositiveInt(row.qty, 0));
  }
  for (const tier of restockNewTiers) {
    for (const item of tier.items) {
      if (!item.productId || !item.qty.trim()) continue;
      totals.set(item.productId, (totals.get(item.productId) ?? 0) + parsePositiveInt(item.qty, 0));
    }
  }
  return totals;
}

function warehouseStockLabel(
  productId: string,
  restockTotal: number,
  warehouseByProductId: Map<string, number>,
  productsById: Map<string, ProductRef>,
): { available: number; over: boolean; label: string } {
  const product = productsById.get(productId);
  const available = product?.stockAvailable ?? warehouseByProductId.get(productId) ?? 0;
  const tracks = product ? productTracksStock(product) : warehouseByProductId.has(productId);
  if (!tracks) {
    return { available: 999_999, over: false, label: "" };
  }
  const over = restockTotal > available;
  return {
    available,
    over,
    label: over
      ? `Adding ${restockTotal} — only ${available} in warehouse`
      : `${available} in warehouse${restockTotal > 0 ? ` · adding ${restockTotal}` : ""}`,
  };
}

export function Packs() {
  const qc = useQueryClient();
  const canWrite = useAuth(
    (s) => !!s.user && (s.user.isSuperadmin || s.user.permissions.includes("packs.write")),
  );
  const { confirm, dialog } = useConfirm();

  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<PackDto | null>(null);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState<string[]>([]);
  const [price, setPrice] = useState("");
  const [creditPrice, setCreditPrice] = useState("");
  const [compareAtPrice, setCompareAtPrice] = useState("");
  const [buybackPercent, setBuybackPercent] = useState("");
  const [weightGrams, setWeightGrams] = useState("100");
  const [listed, setListed] = useState(false);
  const [mode, setMode] = useState<"DECLINING" | "FIXED">("DECLINING");
  const [openOffers, setOpenOffers] = useState<OpenOfferForm[]>([{ ...DEFAULT_OFFER }]);
  const [tiers, setTiers] = useState<TierForm[]>([{ ...DEFAULT_TIER }]);
  const [foilLabel, setFoilLabel] = useState("");
  const [foilColor, setFoilColor] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [addQtyByItem, setAddQtyByItem] = useState<Record<string, string>>({});
  const [restockNewItems, setRestockNewItems] = useState<RestockTierItemForm[]>([]);
  const [restockNewTiers, setRestockNewTiers] = useState<TierForm[]>([]);
  const [relistOnRestock, setRelistOnRestock] = useState(false);

  useEffect(() => {
    const t = globalThis.setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => globalThis.clearTimeout(t);
  }, [productSearch]);


  const { data: artworkAi } = useQuery({
    queryKey: ["packs-artwork-ai"],
    queryFn: () => api<{ available: boolean }>("/packs/admin/artwork-ai"),
    enabled: canWrite,
  });

  const generateArtworkMut = useMutation({
    mutationFn: (payload: { id: string; foilLabel: string | null; foilColor: string | null }) =>
      api<{ url: string; images: string[] }>(`/packs/${payload.id}/generate-artwork`, {
        method: "POST",
        body: {
          foilLabel: payload.foilLabel,
          foilColor: payload.foilColor,
        },
      }),
    onSuccess: (data) => {
      setImages(data.images);
      toast("success", "Pack artwork generated");
      void qc.invalidateQueries({ queryKey: ["packs-admin"] });
      if (editId) void qc.invalidateQueries({ queryKey: ["pack-detail", editId] });
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Artwork generation failed"),
  });

  function runGenerateArtwork(id: string) {
    generateArtworkMut.mutate({
      id,
      foilLabel: foilLabel.trim() || null,
      foilColor: foilColor.trim() || null,
    });
  }

  const { data, isLoading } = useQuery({
    queryKey: ["packs-admin"],
    queryFn: () => api<{ items: PackDto[] }>("/packs/admin"),
  });

  const catalogProducts = useInfiniteCatalogProducts({
    enabled: createOpen || !!editId,
    search: debouncedProductSearch,
    queryKey: ["pack-pool"],
    params: { forMechanics: "true", behavior: "SINGLE" },
  });

  const poolProductOptions = useMemo(
    () =>
      catalogProducts.items.map((p) => ({
        value: p.id,
        label: p.name,
        hint: productTracksStock(p) ? `${p.stockAvailable ?? 0} in stock` : undefined,
        image: p.images?.[0] ?? null,
      })),
    [catalogProducts.items],
  );

  const productsById = useMemo(
    () => new Map(catalogProducts.items.map((p) => [p.id, p])),
    [catalogProducts.items],
  );

  const poolQtyTotals = useMemo(() => poolQtyByProduct(tiers), [tiers]);

  const { data: packDetail, isLoading: detailLoading } = useQuery({
    queryKey: ["pack-detail", editId],
    queryFn: () => api<{ pack: PackDetail }>(`/packs/${editId}`),
    enabled: !!editId && !createOpen,
  });

  useEffect(() => {
    if (!editId || createOpen || !packDetail?.pack) return;
    const detail = packDetail.pack;
    setName(detail.name);
    setSlug(detail.slug);
    setDescription(detail.description ?? "");
    setImages(detail.images);
    setPrice(String(detail.price));
    setCreditPrice(detail.creditPrice != null ? String(detail.creditPrice) : "");
    setCompareAtPrice(detail.compareAtPrice != null ? String(detail.compareAtPrice) : "");
    setBuybackPercent(detail.buybackPercent != null ? String(detail.buybackPercent) : "");
    setWeightGrams(String(detail.weightGrams));
    setListed(detail.listStatus === "LISTED");
    setMode(detail.mode);
    setFoilLabel(detail.foilLabel ?? "");
    setFoilColor(detail.foilColor ?? "");
    setOpenOffers(
      detail.openOffers.length
        ? detail.openOffers.map((o) => ({
            quantity: String(o.quantity),
            price: o.price != null ? String(o.price) : "",
            creditPrice: o.creditPrice != null ? String(o.creditPrice) : "",
          }))
        : [{ ...DEFAULT_OFFER }],
    );
    setTiers(
      detail.tiers.length
        ? detail.tiers.map((t) => ({
            name: t.name,
            color: t.color,
            weight: String(t.weight),
            items: t.items.map((i) => ({ productId: i.productId, qty: String(i.initialQty) })),
          }))
        : [{ ...DEFAULT_TIER }],
    );
  }, [editId, createOpen, packDetail]);

  const saveMut = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) return api(`/packs/${payload.id}`, { method: "PUT", body: payload.body });
      return api<{ id: string }>("/packs", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["packs-admin"] });
      void qc.invalidateQueries({ queryKey: ["pack-detail"] });
      void qc.invalidateQueries({ queryKey: ["inventory-movements"] });
      void qc.invalidateQueries({ queryKey: ["inventory-allocations"] });
      void qc.invalidateQueries({ queryKey: ["inventory-breakdown"] });
      void qc.invalidateQueries({ queryKey: ["catalog-products-all"] });
      toast("success", editId && !createOpen ? "Pack updated" : "Pack created");
      closePanels();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Save failed"),
  });

  const delistMut = useMutation({
    mutationFn: (id: string) => api(`/packs/${id}/delist`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["packs-admin"] });
      void qc.invalidateQueries({ queryKey: ["pack-detail"] });
      toast("success", "Pack delisted");
      closePanels();
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Delist failed"),
  });

  const restockMut = useMutation({
    mutationFn: (payload: { id: string; body: Record<string, unknown> }) =>
      api(`/packs/${payload.id}/restock`, { method: "POST", body: payload.body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["packs-admin"] });
      void qc.invalidateQueries({ queryKey: ["pack-detail"] });
      void qc.invalidateQueries({ queryKey: ["inventory-movements"] });
      void qc.invalidateQueries({ queryKey: ["inventory-allocations"] });
      void qc.invalidateQueries({ queryKey: ["inventory-breakdown"] });
      void qc.invalidateQueries({ queryKey: ["catalog-products-all"] });
      toast("success", "Pool restocked");
      setAddQtyByItem({});
      setRestockNewItems([]);
      setRestockNewTiers([]);
      setRelistOnRestock(false);
    },
    onError: (e) => toast("error", e instanceof ApiError ? e.message : "Restock failed"),
  });

  function resetForm() {
    setName("");
    setSlug("");
    setDescription("");
    setImages([]);
    setPrice("");
    setCreditPrice("");
    setCompareAtPrice("");
    setBuybackPercent("");
    setWeightGrams("100");
    setListed(false);
    setMode("DECLINING");
    setOpenOffers([{ ...DEFAULT_OFFER }]);
    setTiers([{ ...DEFAULT_TIER }]);
    setFoilLabel("");
    setFoilColor("");
    setAddQtyByItem({});
    setRestockNewItems([]);
    setRestockNewTiers([]);
    setRelistOnRestock(false);
  }

  function closePanels() {
    setCreateOpen(false);
    setEditId(null);
    setEditRow(null);
    resetForm();
  }

  function openCreate() {
    setEditId(null);
    setEditRow(null);
    resetForm();
    setCreateOpen(true);
  }

  function openEdit(row: PackDto) {
    setCreateOpen(false);
    setEditRow(row);
    setEditId(row.id);
  }

  const tableRowActions = useMemo(
    () => (canWrite ? { onEdit: openEdit } : undefined),
    [canWrite],
  );

  const tableSelection = useMemo(
    () =>
      canWrite
        ? {
            entityLabel: "Pack",
            onEdit: (rows: PackDto[]) => openEdit(rows[0]!),
          }
        : undefined,
    [canWrite],
  );

  const editingExisting = !!editId && !createOpen;
  const showEditor = createOpen || editingExisting;
  const detailReady = !editingExisting || (!detailLoading && !!packDetail?.pack);
  const poolLocked =
    editingExisting &&
    (editRow?.hasPulls ||
      packDetail?.pack.hasPulls ||
      (packDetail?.pack.totalPulled ?? 0) > 0 ||
      packDetail?.pack.listStatus === "COMPLETE" ||
      packDetail?.pack.listStatus === "DELISTED");
  const useRestockMode = editingExisting && detailReady && poolLocked;
  const poolEmpty = (packDetail?.pack.totalRemaining ?? 0) <= 0;

  const packCommittedQty = useMemo(
    () => committedQtyByProduct(editingExisting ? packDetail?.pack : undefined),
    [editingExisting, packDetail?.pack],
  );

  const warehouseByProductId = useMemo(() => {
    const map = new Map<string, number>();
    for (const tier of packDetail?.pack.tiers ?? []) {
      for (const item of tier.items) {
        map.set(item.productId, item.warehouseAvailable);
      }
    }
    for (const product of catalogProducts.items) {
      if (productTracksStock(product)) {
        map.set(product.id, product.stockAvailable ?? 0);
      }
    }
    return map;
  }, [packDetail?.pack.tiers, catalogProducts.items]);

  const restockQtyTotals = useMemo(
    () => restockQtyByProduct(packDetail?.pack, addQtyByItem, restockNewItems, restockNewTiers),
    [packDetail?.pack, addQtyByItem, restockNewItems, restockNewTiers],
  );

  const restockHasStockError = useMemo(() => {
    for (const [productId, needed] of restockQtyTotals) {
      const { over } = warehouseStockLabel(productId, needed, warehouseByProductId, productsById);
      if (over) return true;
    }
    return false;
  }, [restockQtyTotals, warehouseByProductId, productsById]);

  const foilPreview = useMemo(
    () =>
      resolvePackFoil({
        id: editId ?? "preview-pack",
        foilLabel: foilLabel.trim() || null,
        foilColor: foilColor.trim() || null,
      }),
    [editId, foilLabel, foilColor],
  );

  function addTier() {
    setTiers((t) => [...t, { ...DEFAULT_TIER, name: `Tier ${t.length + 1}` }]);
  }

  function removeTier(index: number) {
    setTiers((t) => t.filter((_, i) => i !== index));
  }

  function updateTier(index: number, patch: Partial<TierForm>) {
    setTiers((t) => t.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));
  }

  function addTierItem(tierIndex: number) {
    setTiers((t) =>
      t.map((tier, i) =>
        i === tierIndex ? { ...tier, items: [...tier.items, { productId: "", qty: "1" }] } : tier,
      ),
    );
  }

  function updateTierItem(tierIndex: number, itemIndex: number, patch: Partial<TierItem>) {
    setTiers((t) =>
      t.map((tier, i) =>
        i === tierIndex
          ? {
              ...tier,
              items: tier.items.map((item, j) => (j === itemIndex ? { ...item, ...patch } : item)),
            }
          : tier,
      ),
    );
  }

  function removeTierItem(tierIndex: number, itemIndex: number) {
    setTiers((t) =>
      t.map((tier, i) =>
        i === tierIndex ? { ...tier, items: tier.items.filter((_, j) => j !== itemIndex) } : tier,
      ),
    );
  }

  function updateRestockTier(index: number, patch: Partial<TierForm>) {
    setRestockNewTiers((rows) => rows.map((tier, i) => (i === index ? { ...tier, ...patch } : tier)));
  }

  function addRestockTierItem(tierIndex: number) {
    setRestockNewTiers((rows) =>
      rows.map((tier, i) =>
        i === tierIndex ? { ...tier, items: [...tier.items, { productId: "", qty: "1" }] } : tier,
      ),
    );
  }

  function updateRestockTierItem(tierIndex: number, itemIndex: number, patch: Partial<TierItem>) {
    setRestockNewTiers((rows) =>
      rows.map((tier, i) =>
        i === tierIndex
          ? {
              ...tier,
              items: tier.items.map((item, j) => (j === itemIndex ? { ...item, ...patch } : item)),
            }
          : tier,
      ),
    );
  }

  function removeRestockTierItem(tierIndex: number, itemIndex: number) {
    setRestockNewTiers((rows) =>
      rows.map((tier, i) =>
        i === tierIndex ? { ...tier, items: tier.items.filter((_, j) => j !== itemIndex) } : tier,
      ),
    );
  }

  function addOfferPreset(qty: number) {
    setOpenOffers((offers) => {
      if (offers.some((o) => parsePositiveInt(o.quantity, 0) === qty)) return offers;
      return [...offers, { quantity: String(qty), price: "", creditPrice: "" }];
    });
  }

  function buildPayload() {
    if (!useRestockMode) {
      const totals = poolQtyByProduct(tiers);
      for (const [productId, needed] of totals) {
        const product = productsById.get(productId);
        if (!product || !productTracksStock(product)) continue;
        const available = effectiveStockForPool(product, packCommittedQty);
        if (needed > available) {
          toast(
            "error",
            `Insufficient stock for ${product.name} — pool needs ${needed}, only ${available} available`,
          );
          return null;
        }
      }
    }

    if (!name.trim()) {
      toast("error", "Enter a pack name");
      return null;
    }
    const parsedPrice = Number.parseFloat(price);
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      toast("error", "Enter a valid base price");
      return null;
    }

    const offerPayload = openOffers
      .filter((o) => o.quantity.trim())
      .map((o, index) => ({
        quantity: parsePositiveInt(o.quantity, 0),
        price: o.price.trim() ? Number.parseFloat(o.price) : null,
        creditPrice: o.creditPrice.trim() ? Number.parseFloat(o.creditPrice) : null,
        sortOrder: index,
      }))
      .filter((o) => o.quantity >= 1);

    if (!offerPayload.length) {
      toast("error", "Add at least one open offer");
      return null;
    }

    const tierPayload = useRestockMode
      ? []
      : tiers.map((t) => ({
          name: t.name.trim(),
          color: t.color,
          weight: parsePositiveInt(t.weight),
          items: t.items
            .filter((i) => i.productId && i.qty !== "")
            .map((i) => ({ productId: i.productId, qty: parsePositiveInt(i.qty) })),
        }));

    const includeTiers = createOpen || !poolLocked;
    const tiersForSave = tierPayload.filter((t) => t.name && t.items.length > 0);

    if (includeTiers && !tiersForSave.length) {
      toast("error", "Add at least one pool tier with at least one product");
      return null;
    }

    return {
      name: name.trim(),
      slug: slug.trim() || undefined,
      description: description.trim() || null,
      images,
      price: parsedPrice,
      creditPrice: creditPrice.trim() ? Number.parseFloat(creditPrice) : null,
      compareAtPrice: compareAtPrice.trim() ? Number.parseFloat(compareAtPrice) : null,
      buybackPercent: buybackPercent.trim() ? parsePositiveInt(buybackPercent, 0) : null,
      weightGrams: parsePositiveInt(weightGrams, 100),
      mode,
      listStatus: listed ? "LISTED" : "DRAFT",
      foilLabel: foilLabel.trim() || null,
      foilColor: foilColor.trim() || null,
      openOffers: offerPayload,
      ...(includeTiers ? { tiers: tiersForSave } : {}),
    };
  }

  function validateRestockStock(): boolean {
    for (const [productId, needed] of restockQtyTotals) {
      const product = productsById.get(productId);
      const available = product?.stockAvailable ?? warehouseByProductId.get(productId) ?? 0;
      const tracks = product ? productTracksStock(product) : warehouseByProductId.has(productId);
      if (!tracks) continue;
      if (needed > available) {
        const name =
          product?.name ??
          packDetail?.pack.tiers.flatMap((t) => t.items).find((i) => i.productId === productId)?.name ??
          "Product";
        toast("error", `Insufficient stock for ${name} — adding ${needed}, only ${available} in warehouse`);
        return false;
      }
    }
    return true;
  }

  function buildRestockPayload() {
    const addQty = Object.entries(addQtyByItem)
      .filter(([, qty]) => qty.trim())
      .map(([packItemId, qty]) => ({ packItemId, addQty: parsePositiveInt(qty, 0) }))
      .filter((row) => row.addQty > 0);

    const newTierItems = restockNewItems
      .filter((row) => row.tierId && row.productId && row.qty.trim())
      .map((row) => ({
        tierId: row.tierId,
        productId: row.productId,
        qty: parsePositiveInt(row.qty, 0),
      }))
      .filter((row) => row.qty > 0);

    const newTiers = restockNewTiers
      .map((t) => ({
        name: t.name,
        color: t.color,
        weight: parsePositiveInt(t.weight),
        items: t.items
          .filter((i) => i.productId && i.qty !== "")
          .map((i) => ({ productId: i.productId, qty: parsePositiveInt(i.qty) })),
      }))
      .filter((t) => t.name.trim() && t.items.length > 0);

    if (!addQty.length && !newTierItems.length && !newTiers.length && !relistOnRestock) {
      toast("error", "Add pool stock or enable relist");
      return null;
    }

    return {
      ...(addQty.length ? { addQty } : {}),
      ...(newTierItems.length ? { newTierItems } : {}),
      ...(newTiers.length ? { newTiers } : {}),
      ...(relistOnRestock ? { relist: true } : {}),
    };
  }

  function submit() {
    if (editingExisting && !detailReady) {
      toast("error", "Wait for the pack to finish loading");
      return;
    }
    const body = buildPayload();
    if (!body) return;
    if (createOpen) {
      if (!body.tiers?.length) {
        toast("error", "Add at least one pool tier with at least one product");
        return;
      }
      saveMut.mutate({ body });
    } else if (editId) {
      saveMut.mutate({ id: editId, body });
    }
  }

  function submitRestock() {
    if (!editId) return;
    if (!validateRestockStock()) return;
    const body = buildRestockPayload();
    if (!body) return;
    restockMut.mutate({ id: editId, body });
  }

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (showEditor) {
      return [
        { id: "cancel", label: "Cancel", onClick: closePanels },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled: saveMut.isPending || (editingExisting && !detailReady),
          onClick: submit,
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const columns: Column<PackDto>[] = [
    {
      key: "name",
      header: "Pack",
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => <span className="font-medium">{r.name}</span>,
    },
    {
      key: "mode",
      header: "Mode",
      sortable: true,
      sortValue: (r) => r.mode,
      render: (r) => <Badge color="blue">{r.mode}</Badge>,
    },
    {
      key: "remaining",
      header: "Remaining",
      className: "text-right",
      sortable: true,
      sortValue: (r) => r.totalRemaining,
      render: (r) => (
        <span className="tabular-nums">
          {num(r.totalPulled)} / {num(r.totalInitial)}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.listStatus,
      render: (r) => {
        const meta = STATUS_BADGE[r.listStatus];
        return <Badge color={meta.color}>{meta.label}</Badge>;
      },
    },
    {
      key: "topTier",
      header: "Top tier",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.topTier ?? "",
      render: (r) => r.topTier ?? "—",
    },
  ];

  return (
    <ListPageShell>
      {dialog}

      <DataTable
        columns={columns}
        rows={data?.items}
        rowKey={(r) => r.id}
        loading={isLoading}
        exportFilename="packs"
        entityLabel="Pack"
        rowActions={tableRowActions}
        selection={tableSelection}
        onRowClick={canWrite ? openEdit : undefined}
        activeKey={editId ?? undefined}
        empty={{
          icon: Gift,
          title: "No packs configured",
          hint: canWrite ? "Create a mystery pack listing with pool tiers and bulk open offers." : undefined,
          action: canWrite ? (
            <Button size="sm" onClick={openCreate}>
              <Plus size={14} /> Create
            </Button>
          ) : undefined,
        }}
      />

      <RightSidebarPanel
        open={showEditor}
        wide
        title={createOpen ? "New pack" : packDetail?.pack.name ?? "Edit pack"}
        subtitle={
          createOpen
            ? "Listing, offers, and pool"
            : `${packDetail?.pack.totalRemaining ?? 0} opens left · ${STATUS_BADGE[packDetail?.pack.listStatus ?? "DRAFT"].label}`
        }
        onClose={closePanels}
        footer={
          canWrite ? (
            <div className="flex flex-wrap justify-end gap-2">
              {editingExisting && packDetail?.pack.listStatus === "LISTED" && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={delistMut.isPending}
                  onClick={() =>
                    confirm({
                      title: "Delist this pack?",
                      message: "The pack will disappear from the storefront. Pool commitments will be released.",
                      danger: true,
                      confirmLabel: "Delist",
                      action: async () => delistMut.mutateAsync(packDetail.pack.id),
                    })
                  }
                >
                  Delist
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closePanels}>
                Cancel
              </Button>
              <Button size="sm" disabled={saveMut.isPending || (editingExisting && !detailReady)} onClick={submit}>
                {saveMut.isPending ? "Saving…" : "Save"}
              </Button>
              {useRestockMode && (
                <Button
                  size="sm"
                  variant="primary"
                  disabled={restockMut.isPending || restockHasStockError}
                  onClick={submitRestock}
                >
                  {restockMut.isPending ? "Restocking…" : "Restock"}
                </Button>
              )}
            </div>
          ) : undefined
        }
      >
        {editingExisting && detailLoading && !packDetail ? (
          <p className="text-center text-xs text-white/50">Loading pack…</p>
        ) : (
          <div className="sidebar-form">
            {packDetail?.pack.listStatus === "COMPLETE" && poolEmpty && (
              <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                Pool empty — restock below to reopen this pack on the storefront.
              </p>
            )}
            {packDetail?.pack.listStatus === "DELISTED" && (
              <p className="rounded-xl border border-brand-pink/30 bg-brand-pink/10 px-3 py-2 text-xs text-brand-pink/90">
                Delisted — restock and relist to show on the storefront again.
              </p>
            )}

            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Listing</h4>
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input
              label="Slug"
              hint="Leave blank to auto-generate from name"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <ImageUploadField
              label="Images"
              hint="Up to 5 pack artwork images — first image is shown on the storefront"
              value={images}
              onChange={setImages}
            />
            {images[0] ? (
              <div className="overflow-hidden rounded-xl border border-line bg-black/20 p-3">
                <p className="mb-2 text-[10px] uppercase tracking-wider text-white/40">Artwork preview</p>
                <img src={images[0]} alt="" className="mx-auto h-48 w-auto object-contain" />
              </div>
            ) : (
              <div
                className="flex h-32 items-center justify-center overflow-hidden rounded-xl border border-dashed border-line bg-white/[0.02] text-[10px] text-white/35"
                style={{
                  background: `linear-gradient(120deg, ${foilPreview.gradient.from} 0%, ${foilPreview.gradient.via} 55%, ${foilPreview.gradient.to} 100%)`,
                }}
              >
                CSS foil fallback (no artwork yet)
              </div>
            )}
            <div className="space-y-2">
              <p className="text-[10px] leading-relaxed text-muted">
                AI generates a transparent PNG from your foil label, foil gradient colours, pack name, and company logo.
                Configure an Image Gen model in Settings → Mystery packs. Upload manually to override.
              </p>
              {!artworkAi?.available && (
                <p className="text-[10px] text-amber-400/90">
                  No Image Gen model configured — add one under Models & Knowledge, then select it in Settings.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!editingExisting || !artworkAi?.available || generateArtworkMut.isPending}
                  onClick={() => {
                    if (!editId) return;
                    if (images[0]) {
                      confirm({
                        title: "Regenerate artwork?",
                        message: "This replaces the current pack image with AI-generated artwork from your branding fields.",
                        confirmLabel: "Regenerate",
                        action: () => runGenerateArtwork(editId),
                      });
                      return;
                    }
                    runGenerateArtwork(editId);
                  }}
                >
                  {generateArtworkMut.isPending
                    ? "Generating…"
                    : images[0]
                      ? "Regenerate artwork"
                      : "Generate artwork"}
                </Button>
              </div>
            </div>

            <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Pack branding</h4>
            <p className="text-[10px] leading-relaxed text-white/40">
              Foil strip label and colour on the sealed pack wrapper in the storefront and opening ceremony. Leave blank
              for an auto-assigned colourway.
            </p>
            <div className="form-row-2">
              <Input
                label="Foil label"
                hint="e.g. Gold, Limited"
                value={foilLabel}
                onChange={(e) => setFoilLabel(e.target.value)}
              />
              <ColorField
                label="Foil colour"
                value={foilColor || "#5460ed"}
                onChange={setFoilColor}
              />
            </div>
            <div
              className="flex h-16 items-center overflow-hidden rounded-xl border border-line"
              style={{
                background: `linear-gradient(120deg, ${foilPreview.gradient.from} 0%, ${foilPreview.gradient.via} 55%, ${foilPreview.gradient.to} 100%)`,
              }}
            >
              <span className="pl-3 font-display text-[9px] font-black uppercase tracking-[0.35em] text-white/80">
                {foilPreview.label}
              </span>
            </div>

            <div className="form-row-2">
              <NumberInput
                label="Base price (MYR)"
                decimal
                value={price}
                onValueChange={setPrice}
              />
              <NumberInput
                label="Credit price"
                hint="Per open; blank uses conversion"
                decimal
                value={creditPrice}
                onValueChange={setCreditPrice}
              />
            </div>
            <div className="form-row-2">
              <NumberInput
                label="Compare-at"
                decimal
                value={compareAtPrice}
                onValueChange={setCompareAtPrice}
              />
              <NumberInput
                label="Weight (g)"
                value={weightGrams}
                onValueChange={setWeightGrams}
              />
            </div>
            <NumberInput
              label="Buyback %"
              hint="Percent of pool item est. value (or product price) paid as credits on buyback. Snapshotted at pull — changes only affect future pulls."
              value={buybackPercent}
              onValueChange={setBuybackPercent}
            />
            <Toggle
              label="Listed on storefront"
              checked={listed}
              onChange={setListed}
              disabled={useRestockMode && poolEmpty}
            />
            {!useRestockMode && packDetail?.pack.listStatus === "DRAFT" && (
              <p className="text-[10px] text-white/40">Draft packs are hidden from customers</p>
            )}
            {useRestockMode && (packDetail?.pack.listStatus === "DELISTED" || packDetail?.pack.listStatus === "COMPLETE") && (
              <Toggle
                label="Relist on restock"
                checked={relistOnRestock}
                onChange={setRelistOnRestock}
              />
            )}

            <div className="flex items-center justify-between pt-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Open offers</h4>
              <div className="flex gap-1">
                {[3, 10].map((q) => (
                  <Button key={q} variant="ghost" size="sm" onClick={() => addOfferPreset(q)}>
                    +×{q}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-white/40">
              Cash/credit totals for bulk opens. Leave blank to use base price × quantity.
            </p>
            {openOffers.map((offer, oi) => (
              <div key={oi} className="form-row-offer">
                <NumberInput
                  label={oi === 0 ? "Qty" : undefined}
                  value={offer.quantity}
                  onValueChange={(quantity) =>
                    setOpenOffers((rows) => rows.map((r, i) => (i === oi ? { ...r, quantity } : r)))
                  }
                />
                <NumberInput
                  label={oi === 0 ? "Cash total" : undefined}
                  decimal
                  value={offer.price}
                  onValueChange={(priceVal) =>
                    setOpenOffers((rows) => rows.map((r, i) => (i === oi ? { ...r, price: priceVal } : r)))
                  }
                />
                <NumberInput
                  label={oi === 0 ? "Credit total" : undefined}
                  decimal
                  value={offer.creditPrice}
                  onValueChange={(creditVal) =>
                    setOpenOffers((rows) => rows.map((r, i) => (i === oi ? { ...r, creditPrice: creditVal } : r)))
                  }
                />
                {openOffers.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="form-row-offer__remove"
                    onClick={() => setOpenOffers((rows) => rows.filter((_, i) => i !== oi))}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenOffers((rows) => [...rows, { quantity: "", price: "", creditPrice: "" }])}
            >
              Add offer
            </Button>

            <SearchSelect
              label="Mode"
              options={[...MODE_OPTIONS]}
              value={mode}
              onChange={(v) => setMode(v as "DECLINING" | "FIXED")}
            />

            {useRestockMode && packDetail ? (
              <>
                <div className="flex items-center justify-between pt-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Restock pool</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setRestockNewTiers((rows) => [...rows, { ...DEFAULT_TIER, name: `Tier ${rows.length + 1}` }])}
                  >
                    Add tier
                  </Button>
                </div>
                <p className="text-[10px] text-white/40">
                  Add qty to existing lines or append new singles/tiers. Cards stay locked in the pool when listed.
                </p>

                {packDetail.pack.tiers.map((tier) => (
                  <div key={tier.id} className="form-stack-tight rounded-xl border border-line bg-white/[0.02] p-3">
                    <p className="text-xs font-semibold" style={{ color: tier.color }}>
                      {tier.name}
                    </p>
                    {tier.items.map((item) => {
                      const restockTotal = restockQtyTotals.get(item.productId) ?? 0;
                      const stock = warehouseStockLabel(
                        item.productId,
                        restockTotal,
                        warehouseByProductId,
                        productsById,
                      );
                      const addQty = addQtyByItem[item.id] ?? "";
                      const addOver =
                        stock.over && addQty.trim() && parsePositiveInt(addQty, 0) > 0;
                      return (
                      <div key={item.id} className="flex items-center gap-2 border-t border-line/40 pt-2 first:border-0 first:pt-0">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs">{item.name}</p>
                          <p className="text-[10px] text-white/45">
                            {item.remainingQty} left · {item.initialQty - item.remainingQty} pulled · {item.initialQty} total
                          </p>
                          <p className={`text-[10px] ${stock.over ? "text-red-400" : "text-white/40"}`}>
                            {stock.label || `${item.warehouseAvailable} in warehouse`}
                          </p>
                        </div>
                        <NumberInput
                          className={`w-20 ${addOver ? "border-red-400/60 focus:border-red-400" : ""}`}
                          label="Add"
                          value={addQty}
                          onValueChange={(qty) => setAddQtyByItem((prev) => ({ ...prev, [item.id]: qty }))}
                          blurFallback=""
                          aria-invalid={addOver || undefined}
                          hint={addOver ? `Max ${stock.available}` : undefined}
                        />
                      </div>
                      );
                    })}
                  </div>
                ))}

                <div className="flex items-center justify-between pt-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Add to tier</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setRestockNewItems((rows) => [
                        ...rows,
                        { tierId: packDetail.pack.tiers[0]?.id ?? "", productId: "", qty: "1" },
                      ])
                    }
                  >
                    Add row
                  </Button>
                </div>
                {restockNewItems.map((row, ri) => {
                  const restockTotal = row.productId ? (restockQtyTotals.get(row.productId) ?? 0) : 0;
                  const stock = row.productId
                    ? warehouseStockLabel(row.productId, restockTotal, warehouseByProductId, productsById)
                    : null;
                  const rowOver = stock?.over ?? false;
                  return (
                  <div key={ri} className="space-y-1">
                    <div className="flex gap-2">
                    <SearchSelect
                      className="w-36 shrink-0"
                      label={ri === 0 ? "Tier" : undefined}
                      options={packDetail.pack.tiers.map((t) => ({ value: t.id, label: t.name }))}
                      value={row.tierId}
                      onChange={(tierId) =>
                        setRestockNewItems((rows) => rows.map((r, i) => (i === ri ? { ...r, tierId } : r)))
                      }
                    />
                    <SearchSelect
                      className="min-w-0 flex-1"
                      label={ri === 0 ? "Product" : undefined}
                      placeholder="Product…"
                      searchPlaceholder="Search products…"
                      options={poolProductOptions}
                      value={row.productId}
                      onChange={(productId) =>
                        setRestockNewItems((rows) => rows.map((r, i) => (i === ri ? { ...r, productId } : r)))
                      }
                      onSearchChange={setProductSearch}
                      serverFiltered
                      hasMore={catalogProducts.hasNextPage}
                      loadingMore={catalogProducts.isFetchingNextPage}
                      onLoadMore={catalogProducts.fetchNextPage}
                    />
                    <NumberInput
                      className={`w-20 ${rowOver ? "border-red-400/60 focus:border-red-400" : ""}`}
                      label={ri === 0 ? "Qty" : undefined}
                      value={row.qty}
                      onValueChange={(qty) =>
                        setRestockNewItems((rows) => rows.map((r, i) => (i === ri ? { ...r, qty } : r)))
                      }
                      blurFallback="1"
                      aria-invalid={rowOver || undefined}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setRestockNewItems((rows) => rows.filter((_, i) => i !== ri))}>
                      <X size={14} />
                    </Button>
                    </div>
                    {stock?.label && (
                      <p className={`truncate text-[10px] ${stock.over ? "text-red-400" : "text-white/40"}`}>
                        {stock.label}
                      </p>
                    )}
                  </div>
                  );
                })}

                {restockNewTiers.map((tier, ti) => (
                  <div key={ti} className="form-stack-tight rounded-xl border border-dashed border-line p-3">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-semibold uppercase text-white/40">New tier</p>
                      <Button variant="ghost" size="sm" onClick={() => setRestockNewTiers((rows) => rows.filter((_, i) => i !== ti))}>
                        <X size={14} />
                      </Button>
                    </div>
                    <Input label="Tier name" value={tier.name} onChange={(e) => updateRestockTier(ti, { name: e.target.value })} />
                    <div className="form-row-2">
                      <NumberInput label="Weight" value={tier.weight} onValueChange={(weight) => updateRestockTier(ti, { weight })} />
                      <ColorField label="Color" value={tier.color} onChange={(color) => updateRestockTier(ti, { color })} />
                    </div>
                    {tier.items.map((item, ii) => {
                      const restockTotal = item.productId ? (restockQtyTotals.get(item.productId) ?? 0) : 0;
                      const stock = item.productId
                        ? warehouseStockLabel(item.productId, restockTotal, warehouseByProductId, productsById)
                        : null;
                      const rowOver = stock?.over ?? false;
                      return (
                      <div key={ii} className="space-y-1">
                        <div className="flex gap-2">
                        <SearchSelect
                          className="min-w-0 flex-1"
                          options={poolProductOptions}
                          value={item.productId}
                          onChange={(v) => updateRestockTierItem(ti, ii, { productId: v })}
                          onSearchChange={setProductSearch}
                          serverFiltered
                          hasMore={catalogProducts.hasNextPage}
                          loadingMore={catalogProducts.isFetchingNextPage}
                          onLoadMore={catalogProducts.fetchNextPage}
                        />
                        <NumberInput
                          className={`w-20 ${rowOver ? "border-red-400/60 focus:border-red-400" : ""}`}
                          value={item.qty}
                          onValueChange={(qty) => updateRestockTierItem(ti, ii, { qty })}
                          blurFallback="1"
                          aria-invalid={rowOver || undefined}
                        />
                        {tier.items.length > 1 && (
                          <Button variant="ghost" size="sm" onClick={() => removeRestockTierItem(ti, ii)}>
                            <X size={14} />
                          </Button>
                        )}
                        </div>
                        {stock?.label && (
                          <p className={`truncate text-[10px] ${stock.over ? "text-red-400" : "text-white/40"}`}>
                            {stock.label}
                          </p>
                        )}
                      </div>
                      );
                    })}
                    <Button variant="ghost" size="sm" onClick={() => addRestockTierItem(ti)}>
                      Add item
                    </Button>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between pt-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-white/40">Pool tiers</h4>
                  <Button variant="ghost" size="sm" onClick={addTier}>
                    Add tier
                  </Button>
                </div>

                {tiers.map((tier, ti) => (
                  <div key={ti} className="form-stack-tight rounded-xl border border-line bg-white/[0.02] p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center rounded-full border px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide"
                        style={{
                          color: tier.color,
                          borderColor: `${tier.color}66`,
                          backgroundColor: `${tier.color}22`,
                        }}
                      >
                        {tier.name.trim() || "Tier"}
                      </span>
                      <span className="text-[10px] text-white/35">Storefront badge preview</span>
                    </div>
                    <div className="form-row-inline">
                      <div className="field-grow">
                        <Input
                          label="Tier name"
                          value={tier.name}
                          onChange={(e) => updateTier(ti, { name: e.target.value })}
                        />
                      </div>
                      {tiers.length > 1 && (
                        <Button
                          variant="danger"
                          size="sm"
                          className="mt-5"
                          onClick={() =>
                            confirm({
                              title: "Remove tier?",
                              danger: true,
                              confirmLabel: "Remove",
                              action: async () => removeTier(ti),
                            })
                          }
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                    <div className="form-row-2">
                      <NumberInput
                        label="Weight"
                        value={tier.weight}
                        onValueChange={(weight) => updateTier(ti, { weight })}
                        className="h-[38px] py-0"
                      />
                      <ColorField label="Color" value={tier.color} onChange={(color) => updateTier(ti, { color })} />
                    </div>

                    <p className="text-[10px] font-semibold uppercase text-white/40">Pool items (singles only)</p>
                    {tier.items.map((item, ii) => {
                      const product = item.productId ? productsById.get(item.productId) : undefined;
                      const poolTotal = item.productId ? (poolQtyTotals.get(item.productId) ?? 0) : 0;
                      const effectiveStock = product ? effectiveStockForPool(product, packCommittedQty) : 0;
                      const stockOver =
                        product && productTracksStock(product) && poolTotal > effectiveStock;
                      return (
                        <div key={ii} className="space-y-1">
                          <div className="flex gap-2">
                            <SearchSelect
                              className="min-w-0 flex-1"
                              placeholder="Product…"
                              searchPlaceholder="Search products…"
                              options={poolProductOptions}
                              value={item.productId}
                              onChange={(v) => updateTierItem(ti, ii, { productId: v })}
                              onSearchChange={setProductSearch}
                              serverFiltered
                              hasMore={catalogProducts.hasNextPage}
                              loadingMore={catalogProducts.isFetchingNextPage}
                              onLoadMore={catalogProducts.fetchNextPage}
                            />
                            <NumberInput
                              className={`w-20 ${stockOver ? "border-red-400/60 focus:border-red-400" : ""}`}
                              value={item.qty}
                              onValueChange={(qty) => updateTierItem(ti, ii, { qty })}
                              blurFallback="1"
                              aria-invalid={stockOver || undefined}
                            />
                            {tier.items.length > 1 && (
                              <Button variant="ghost" size="sm" onClick={() => removeTierItem(ti, ii)}>
                                <X size={14} />
                              </Button>
                            )}
                          </div>
                          {product && productTracksStock(product) && (
                            <p className={`truncate text-[10px] ${stockOver ? "text-red-400" : "text-white/40"}`}>
                              {stockOver
                                ? `Pool needs ${poolTotal} — only ${effectiveStock} available`
                                : `${effectiveStock} available · ${poolTotal} in pool`}
                            </p>
                          )}
                        </div>
                      );
                    })}
                    <Button variant="ghost" size="sm" onClick={() => addTierItem(ti)}>
                      Add item
                    </Button>
                  </div>
                ))}
              </>
            )}

            {editingExisting && packDetail && (
              <div className="rounded-xl border border-line/60 p-3 text-xs text-white/50">
                <p>
                  Base per open: {rm(packDetail.pack.price)}
                  {packDetail.pack.creditPrice != null ? ` · ${packDetail.pack.creditPrice} credits` : ""}
                  {packDetail.pack.buybackPercent != null && packDetail.pack.buybackPercent > 0
                    ? ` · ${packDetail.pack.buybackPercent}% buyback`
                    : ""}
                </p>
                {packDetail.pack.tiers.map((t) => (
                  <p key={t.id} className="mt-1">
                    {t.name}: {t.oddsPct}% odds · {t.items.reduce((a, i) => a + i.remainingQty, 0)} remaining
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </RightSidebarPanel>
    </ListPageShell>
  );
}
